import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, User, FileSignature, FileSpreadsheet, FolderOpen, Loader2, Mail, Phone, MapPin, CreditCard, IdCard, Cake, Save, FileDown, Eye, Building2, Users2, Trash2 } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { ContractUploader, FileUploader, deleteContractRow, deleteFileRow } from "./DriveArtistDetail";
import { deleteCustomer, deleteExternal } from "../lib/driveClient";
import { DeleteEntityButton } from "../components/DeleteEntityButton";

const formatEUR = (v) => (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const TABS = [
  { id: "overview", label: "Übersicht", icon: User },
  { id: "contracts", label: "Verträge", icon: FileSignature },
  { id: "invoices", label: "Rechnungen", icon: FileSpreadsheet },
  { id: "files", label: "Dateien", icon: FolderOpen },
];

// type: "customer" | "external"
export default function DriveContactDetail({ type }) {
  const { id } = useParams();
  const isCustomer = type === "customer";
  const table = isCustomer ? "drive_customers" : "drive_externals";
  const fkColumn = isCustomer ? "customer_id" : "external_id";

  const [contact, setContact] = useState(null);
  const [edit, setEdit] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dat, setDat] = useState({ contracts: [], invoices: [], files: [] });
  const [busy, setBusy] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, con, inv, files] = await Promise.all([
        supabase.from(table).select("*").eq("id", id).single(),
        supabase.from("drive_contracts").select("*").eq(fkColumn, id).order("created_at", { ascending: false }),
        supabase.from("drive_invoices").select("*").eq(fkColumn, id).order("invoice_date", { ascending: false }),
        supabase.from("drive_files").select("*").eq(fkColumn, id).order("uploaded_at", { ascending: false }).then(r => r).catch(() => ({ data: [] })),
      ]);
      if (c.error) throw c.error;
      setContact(c.data);
      setEdit(c.data);
      setDat({ contracts: con.data || [], invoices: inv.data || [], files: files.data || [] });
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const saveContact = async () => {
    setSaving(true);
    try {
      const patch = isCustomer ? {
        name: edit.name, company: edit.company, first_name: edit.first_name, last_name: edit.last_name,
        email: edit.email, phone: edit.phone, address: edit.address, zip_city: edit.zip_city,
        birthdate: edit.birthdate || null, tax_id: edit.tax_id, iban: edit.iban, bic: edit.bic, bank_name: edit.bank_name,
        notes: edit.notes,
      } : {
        full_name: edit.full_name, first_name: edit.first_name, last_name: edit.last_name, company: edit.company,
        relation: edit.relation, email: edit.email, phone: edit.phone, address: edit.address, zip_city: edit.zip_city,
        birthdate: edit.birthdate || null, tax_id: edit.tax_id, iban: edit.iban, bic: edit.bic, bank_name: edit.bank_name,
        notes: edit.notes,
      };
      const { error } = await supabase.from(table).update(patch).eq("id", id);
      if (error) throw error;
      toast.success("Gespeichert");
      await loadAll();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading || !contact) {
    return <div className="flex items-center gap-3 text-zinc-400 text-sm py-16"><Loader2 size={16} className="animate-spin" /> Lade…</div>;
  }

  const displayName = isCustomer
    ? (contact.name || [contact.first_name, contact.last_name].filter(Boolean).join(" "))
    : (contact.full_name || [contact.first_name, contact.last_name].filter(Boolean).join(" "));
  const totalInvoiced = dat.invoices.reduce((s, i) => s + Number(i.total || 0), 0);

  return (
    <div className="space-y-8">
      <div>
        <Link to="/drive/crm" className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white font-bold mb-4">
          <ArrowLeft size={12} /> Zurück zum CRM
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-2">{isCustomer ? "Kunde" : "Externer Partner"}</div>
            <div className="font-display font-black uppercase text-3xl md:text-4xl mb-2">{displayName}</div>
            <div className="text-sm text-zinc-400">
              {contact.company ? `${contact.company} · ` : ""}{contact.customer_number || ""}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-right">
            <div><div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Verträge</div><div className="text-xl font-black text-white">{dat.contracts.length}</div></div>
            <div><div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Rechnungen</div><div className="text-xl font-black text-white">{dat.invoices.length}</div></div>
            <div><div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Umsatz</div><div className="text-xl font-black text-[#ff0033]">{formatEUR(totalInvoiced)}</div></div>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <DeleteEntityButton
            label={isCustomer ? "Kunde löschen" : "Externen löschen"}
            entityName={displayName}
            onConfirm={(withFiles) => isCustomer ? deleteCustomer(id, { withFiles }) : deleteExternal(id, { withFiles })}
          />
        </div>
      </div>

      <div className="flex gap-1 border-b border-white/[0.06] overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-[10px] tracking-[0.3em] uppercase font-bold border-b-2 transition-colors whitespace-nowrap ${active ? "border-[#ff0033] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {isCustomer ? (
            <Field icon={<User size={13} />} label="Anzeigename" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} />
          ) : (
            <Field icon={<User size={13} />} label="Anzeigename" value={edit.full_name} onChange={(v) => setEdit({ ...edit, full_name: v })} />
          )}
          <Field icon={<Building2 size={13} />} label={isCustomer ? "Firma" : "Firma / Institution"} value={edit.company || ""} onChange={(v) => setEdit({ ...edit, company: v })} />
          <Field icon={<User size={13} />} label="Vorname" value={edit.first_name || ""} onChange={(v) => setEdit({ ...edit, first_name: v })} />
          <Field icon={<User size={13} />} label="Nachname" value={edit.last_name || ""} onChange={(v) => setEdit({ ...edit, last_name: v })} />
          {!isCustomer && <Field icon={<Users2 size={13} />} label="Beziehung / Rolle" value={edit.relation || ""} onChange={(v) => setEdit({ ...edit, relation: v })} />}
          <Field icon={<Cake size={13} />} label="Geburtsdatum" type="date" value={edit.birthdate || ""} onChange={(v) => setEdit({ ...edit, birthdate: v })} />
          <Field icon={<Mail size={13} />} label="E-Mail" value={edit.email || ""} onChange={(v) => setEdit({ ...edit, email: v })} />
          <Field icon={<Phone size={13} />} label="Telefon" value={edit.phone || ""} onChange={(v) => setEdit({ ...edit, phone: v })} />
          <Field icon={<MapPin size={13} />} label="Adresse" value={edit.address || ""} onChange={(v) => setEdit({ ...edit, address: v })} />
          <Field icon={<MapPin size={13} />} label="PLZ / Ort" value={edit.zip_city || ""} onChange={(v) => setEdit({ ...edit, zip_city: v })} />
          <Field icon={<IdCard size={13} />} label="Steuer-ID" value={edit.tax_id || ""} onChange={(v) => setEdit({ ...edit, tax_id: v })} />
          <Field icon={<CreditCard size={13} />} label="IBAN" value={edit.iban || ""} onChange={(v) => setEdit({ ...edit, iban: v })} />
          <Field icon={<CreditCard size={13} />} label="BIC" value={edit.bic || ""} onChange={(v) => setEdit({ ...edit, bic: v })} />
          <Field icon={<CreditCard size={13} />} label="Bank" value={edit.bank_name || ""} onChange={(v) => setEdit({ ...edit, bank_name: v })} />
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Notizen</label>
            <textarea value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={3} className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
          </div>
          <div className="md:col-span-2">
            <button onClick={saveContact} disabled={saving} data-testid="save-contact" className="inline-flex items-center gap-2 bg-[#ff0033] text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_20px_rgba(255,0,51,0.5)] transition-all disabled:opacity-50">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving ? "Speichere…" : "Änderungen speichern"}
            </button>
          </div>
        </div>
      )}

      {tab === "contracts" && (
        <>
          <ContractUploader entityType={type} entityId={id} onDone={loadAll} />
          <SimpleList rows={dat.contracts.map(c => ({ id: c.id, primary: c.title || c.category, secondary: (c.status || "").toUpperCase(), date: c.created_at, path: c.signed_storage_path || c.storage_path }))} onDelete={async (r) => { await deleteContractRow(r); toast.success("Gelöscht"); await loadAll(); }} empty="Keine Verträge." />
        </>
      )}

      {tab === "invoices" && (
        <SimpleList rows={dat.invoices.map(i => ({ id: i.id, primary: i.invoice_number, secondary: `${(i.kind || "").toUpperCase()} · ${formatEUR(i.total)}`, date: i.invoice_date, path: i.storage_path }))} empty="Keine Rechnungen." />
      )}

      {tab === "files" && (
        <>
          <FileUploader entityType={type} entityId={id} onDone={loadAll} />
          <SimpleList rows={dat.files.map(f => ({ id: f.id, primary: f.name, secondary: `${((f.size || 0) / 1024).toFixed(0)} KB`, date: f.uploaded_at, path: f.storage_path }))} onDelete={async (r) => { await deleteFileRow(r); toast.success("Gelöscht"); await loadAll(); }} empty="Keine Dateien." />
        </>
      )}
    </div>
  );
}

const Field = ({ icon, label, value, onChange, type = "text" }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2 flex items-center gap-2">{icon} {label}</label>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
  </div>
);

const SimpleList = ({ rows, empty, onDelete }) => {
  const [busy, setBusy] = useState(null);
  const preview = async (path) => {
    if (!path) { toast.error("Keine Datei"); return; }
    setBusy(`pv-${path}`);
    try {
      const { data, error } = await supabase.storage.from("drive-files").createSignedUrl(path, 300);
      if (error) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  const download = async (path, name) => {
    if (!path) { toast.error("Keine Datei"); return; }
    setBusy(`dl-${path}`);
    try {
      const { data: blob, error } = await supabase.storage.from("drive-files").download(path);
      if (error) throw error;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name || "download.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  const del = async (row) => {
    if (!window.confirm(`"${row.primary}" wirklich löschen?`)) return;
    setBusy(`del-${row.id}`);
    try { await onDelete(row); } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  if (!rows.length) return <div className="bg-[#0A0A0A] border border-white/[0.06] px-6 py-8 text-center text-sm text-zinc-500 italic">{empty}</div>;
  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] divide-y divide-white/[0.04]">
      {rows.map((r) => (
        <div key={r.id} className="px-5 py-3 grid grid-cols-1 md:grid-cols-[1fr_160px_100px_auto] gap-3 items-center">
          <div className="text-sm text-white font-medium truncate">{r.primary}</div>
          <div className="text-xs text-zinc-400">{r.secondary}</div>
          <div className="text-[10px] text-zinc-500 tracking-wide">{r.date ? new Date(r.date).toLocaleDateString("de-DE") : ""}</div>
          <div className="flex items-center gap-2 justify-end">
            <button onClick={() => preview(r.path)} disabled={busy === `pv-${r.path}`} title="Vorschau" className="inline-flex items-center justify-center p-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors disabled:opacity-40">
              {busy === `pv-${r.path}` ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
            </button>
            <button onClick={() => download(r.path, `${r.primary}.pdf`)} disabled={busy === `dl-${r.path}`} title="Herunterladen" className="inline-flex items-center justify-center p-2 border border-white/15 hover:border-[#ff0033]/50 text-zinc-300 hover:text-white transition-colors disabled:opacity-40">
              {busy === `dl-${r.path}` ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}
            </button>
            {onDelete && (
              <button onClick={() => del(r)} disabled={busy === `del-${r.id}`} title="Löschen" className="inline-flex items-center justify-center p-2 border border-white/10 text-zinc-500 hover:border-[#ff0033]/50 hover:text-[#ff0033] transition-colors disabled:opacity-40">
                {busy === `del-${r.id}` ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
